import type { FC } from 'react'
import { useState } from 'react'
import { Button } from '../Button/Button'
import styles from './CustomWorkflows.module.scss'
import { HtmlPreview } from './HtmlPreview'
import { Spinner } from '../Spinner/Spinner'
import type { OutputRenderer, WorkflowOutputFile } from './outputTypes'
import { useLazyGetRunFileDownloadUrlQuery } from '../../store/customWorkflow/customWorkflows'
import { useResolvedFileUrl, useRunFileContext } from './runFileContext'
import { triggerDownload } from './download'

const asAttachmentUrl = (url: string): string => `${url}${url.includes('?') ? '&' : '?'}as_attachment=true`

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  const units = ['KB', 'MB', 'GB']
  let size = bytes / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(1)} ${units[unit]}`
}

const ResolvedImage: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const { url } = useResolvedFileUrl(file)
  if (!url) return null
  return <img className={styles.outputImage} src={url} alt={file.filename ?? 'image'} />
}

const ResolvedAudio: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const { url } = useResolvedFileUrl(file)
  if (!url) return null
  return <audio className={styles.outputMedia} controls src={url} />
}

const ResolvedVideo: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const { url } = useResolvedFileUrl(file)
  if (!url) return null
  return <video className={styles.outputMedia} controls src={url} />
}

const ResolvedPdf: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const { url, isError, resolved } = useResolvedFileUrl(file)
  if (isError) {
    return <div className='paragraph-small zocks-text-gray-600 py-4 text-center'>Couldn&apos;t load the file preview.</div>
  }
  if (!url) {
    return (
      <div className='d-flex justify-content-center py-4'>
        <Spinner />
      </div>
    )
  }
  if (!resolved) {
    return (
      <a className='paragraph-small d-inline-block py-2' href={url} target='_blank' rel='noreferrer'>
        Open PDF
      </a>
    )
  }
  return (
    <object className={styles.outputPdf} data={url} type='application/pdf' title={file.filename ?? 'PDF'}>
      <a href={url} target='_blank' rel='noreferrer'>
        Open PDF
      </a>
    </object>
  )
}

const isPdf = (file: WorkflowOutputFile): boolean =>
  file.mimeType === 'application/pdf' || (file.extension ?? '').toLowerCase() === 'pdf' || (file.filename ?? '').toLowerCase().endsWith('.pdf')

const FilePreview: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const mimeType = file.mimeType ?? ''
  if (mimeType.startsWith('image/')) {
    return <ResolvedImage file={file} />
  }
  if (mimeType.startsWith('audio/')) {
    return <ResolvedAudio file={file} />
  }
  if (mimeType.startsWith('video/')) {
    return <ResolvedVideo file={file} />
  }
  if (isPdf(file)) {
    return <ResolvedPdf file={file} />
  }
  return null
}

const FileCard: FC<{ file: WorkflowOutputFile }> = ({ file }) => {
  const ctx = useRunFileContext()
  const [trigger] = useLazyGetRunFileDownloadUrlQuery()
  const [loading, setLoading] = useState(false)
  const onOpen = async () => {
    if (loading) {
      return
    }
    if (ctx !== null && file.relatedId !== null) {
      setLoading(true)
      try {
        const result = await trigger({
          accountId: ctx.accountId,
          workflowId: ctx.workflowId,
          runId: ctx.runId,
          fileId: file.relatedId,
          asAttachment: true,
        }).unwrap()
        triggerDownload(result.url, file.filename)
      } catch {
        triggerDownload(asAttachmentUrl(file.url), file.filename)
      } finally {
        setLoading(false)
      }
      return
    }
    triggerDownload(asAttachmentUrl(file.url), file.filename)
  }
  return (
    <div className={styles.outputFileCard}>
      <FilePreview file={file} />
      <div className={styles.outputFileMeta}>
        <span className={styles.outputFileName}>{file.filename ?? 'file'}</span>
        {file.size !== null && <span className={styles.outputFileSize}>{formatBytes(file.size)}</span>}
        <Button variant='secondary' loading={loading} onClick={() => void onOpen()}>
          Open / download
        </Button>
      </div>
    </div>
  )
}

export const HtmlOutput: OutputRenderer = ({ output }) => (output.kind === 'html' ? <HtmlPreview html={output.value} /> : null)

export const FileOutput: OutputRenderer = ({ output }) => (output.kind === 'file' ? <FileCard file={output.value} /> : null)

export const FileListOutput: OutputRenderer = ({ output }) =>
  output.kind === 'file-list' ? (
    <div className={styles.outputFileList}>
      {output.value.map((file, index) => (
        <FileCard key={`${file.url}-${index}`} file={file} />
      ))}
    </div>
  ) : null

export const TextOutput: OutputRenderer = ({ output }) => (output.kind === 'text' ? <pre className={styles.outputText}>{output.value}</pre> : null)

export const JsonOutput: OutputRenderer = ({ output }) =>
  output.kind === 'json' ? <pre className={styles.outputText}>{JSON.stringify(output.value, null, 2)}</pre> : null

export const PrimitiveOutput: OutputRenderer = ({ output }) => {
  if (output.kind === 'number') {
    return <span>{output.value}</span>
  }
  if (output.kind === 'boolean') {
    return <span>{output.value ? 'true' : 'false'}</span>
  }
  if (output.kind === 'empty') {
    return <span className={styles.outputEmpty}>—</span>
  }
  return null
}
